import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

const COLLAPSED_KEY = 'moria:sidebar-collapsed'
const WIDTH_KEY = 'moria:sidebar-width'
// Keep in sync with theme.css design tokens:
//   WIDTH_DEFAULT ↔ --sidebar-width           (240px)
const WIDTH_MIN_EXPANDED = 180 // snap-to-collapsed threshold on drag release
const WIDTH_MAX = 480
const WIDTH_DEFAULT = 240

function readCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY)
    return stored == null ? true : stored === 'true'
  } catch {
    return true
  }
}

function readWidth(): number {
  try {
    const stored = parseInt(localStorage.getItem(WIDTH_KEY) ?? '', 10)
    // Clamp persisted width to the expanded range — drag can go lower but we never save it
    return isNaN(stored) ? WIDTH_DEFAULT : Math.max(WIDTH_MIN_EXPANDED, Math.min(WIDTH_MAX, stored))
  } catch {
    return WIDTH_DEFAULT
  }
}

function persistCollapsed(isCollapsed: boolean): void {
  try {
    if (isCollapsed) {
      localStorage.removeItem(COLLAPSED_KEY)
    } else {
      localStorage.setItem(COLLAPSED_KEY, 'false')
    }
  } catch {
    /* non-fatal */
  }
}

export const useSidebarStore = defineStore('sidebar', () => {
  const isCollapsed = ref<boolean>(readCollapsed())
  const width = ref<number>(readWidth())

  watch(isCollapsed, persistCollapsed)

  function toggle(): void {
    isCollapsed.value = !isCollapsed.value
    if (!isCollapsed.value) width.value = WIDTH_DEFAULT
  }
  function setCollapsed(next: boolean): void {
    isCollapsed.value = next
  }

  function setWidth(n: number): void {
    width.value = Math.max(WIDTH_MIN_EXPANDED, Math.min(WIDTH_MAX, n))
  }

  function persistWidth(): void {
    try {
      localStorage.setItem(WIDTH_KEY, String(width.value))
    } catch {
      /* non-fatal */
    }
  }

  return { isCollapsed, width, toggle, setCollapsed, setWidth, persistWidth }
})
